import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaHeart, FaComment, FaMapMarkerAlt, FaStar, FaTrash,
  FaCheck, FaSearch, FaFilter, FaSyncAlt, FaSpinner,
  FaTimes, FaSortAmountDown, FaSortAlphaDown, FaSortNumericDown,
  FaCalendar, FaUser, FaEye
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import Loader from '../common/Loader';
import './favorites.css';

const Favorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [filteredFavorites, setFilteredFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOnline, setFilterOnline] = useState(false);
  const [filterVerified, setFilterVerified] = useState(false);
  const [filterPremium, setFilterPremium] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [showFilters, setShowFilters] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL || 'https://murima.julesferry-ga.com/uploads';

  // Charger les favoris
// Charger les favoris
const loadFavorites = useCallback(async () => {
  if (!user?.id) {
    console.log('❌ Pas d\'utilisateur connecté');
    return;
  }

  setLoading(true);
  try {
    console.log('📡 Chargement des favoris pour user:', user.id);
    
    const response = await api.get('/get-favorites.php', {
      params: { user_id: user.id }
    });

    console.log('📦 Réponse complète:',  user.id);
    console.log('📦 Données reçues:', response.data);
    console.log('📦 Type de données:', typeof response.data);
    
    // Vérifier si response.data est une chaîne (JSON stringifié)
    let data = response.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
        console.log('📦 Données parsées:', data);
      } catch (e) {
        console.error('❌ Erreur parsing JSON:', e);
      }
    }

    if (data && data.success) {
      const favoritesData = data.favorites || [];
      console.log('📦 Favoris:', favoritesData);
      console.log('📦 Nombre de favoris:', favoritesData.length);
      
      setFavorites(favoritesData);
      
      if (favoritesData.length === 0) {
        console.log('ℹ️ Aucun favori trouvé');
      } else {
        console.log('✅ Premier favori:', favoritesData[0]);
      }
    } else {
      console.error('❌ Erreur API:', data?.error || 'Erreur inconnue');
      toast.error(data?.error || 'Erreur lors du chargement');
    }
  } catch (error) {
    console.error('❌ Erreur réseau:', error);
    toast.error('Erreur réseau');
  } finally {
    setLoading(false);
  }
}, [user?.id]);
  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  // Filtrer et trier
  useEffect(() => {
    console.log('🔄 Filtrage des favoris...');
    let result = [...favorites];

    if (searchTerm) {
      console.log('🔍 Recherche:', searchTerm);
      result = result.filter(fav => 
        fav.pseudo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fav.ville?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterOnline) {
      console.log('📱 Filtre en ligne actif');
      result = result.filter(fav => fav.en_ligne);
    }
    
    if (filterVerified) {
      console.log('✅ Filtre vérifié actif');
      result = result.filter(fav => fav.is_verified);
    }
    
    if (filterPremium) {
      console.log('⭐ Filtre premium actif');
      result = result.filter(fav => fav.is_premium);
    }

    switch (sortBy) {
      case 'name':
        console.log('📝 Tri par nom');
        result.sort((a, b) => (a.pseudo || '').localeCompare(b.pseudo || ''));
        break;
      case 'distance':
        console.log('📍 Tri par distance');
        result.sort((a, b) => (a.distance || 999) - (b.distance || 999));
        break;
      default:
        console.log('📅 Tri par date');
        result.sort((a, b) => new Date(b.date_ajout) - new Date(a.date_ajout));
    }

    console.log(`📊 Résultat: ${result.length} favoris affichés`);
    setFilteredFavorites(result);
  }, [favorites, searchTerm, filterOnline, filterVerified, filterPremium, sortBy]);

  // Supprimer un favori
  const removeFavorite = async (favoriId, e) => {
    e.stopPropagation();
    console.log('🗑️ Suppression du favori:', favoriId);
    
    setRemovingId(favoriId);
    
    try {
      const response = await api.post('/remove-favorite.php', {
        user_id: user.id,
        favori_id: favoriId
      });

      console.log('📦 Réponse suppression:', response.data);

      if (response.data.success) {
        setFavorites(prev => prev.filter(f => f.id !== favoriId));
        toast.success('Favori supprimé');
      } else {
        toast.error(response.data.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('❌ Erreur suppression:', error);
      toast.error('Erreur réseau');
    } finally {
      setRemovingId(null);
    }
  };

  // Rafraîchir
  const refreshFavorites = () => {
    console.log('🔄 Rafraîchissement manuel');
    loadFavorites();
    toast.success('Liste actualisée');
  };

  const getAvatarUrl = (fav) => {
    const avatarUrl = fav.avatar_url || fav.avatar;
    if (!avatarUrl) {
      console.log('🖼️ Pas d\'avatar pour', fav.pseudo);
      return 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e';
    }
    const url = avatarUrl.startsWith('http') ? avatarUrl : `${UPLOADS_URL}/${avatarUrl}`;
    console.log('🖼️ URL avatar:', url);
    return url;
  };

  const formatDistance = (distance) => {
    if (!distance) return null;
    if (distance < 1) return 'À moins de 1 km';
    if (distance === 1) return 'À 1 km';
    return `À ${distance} km`;
  };

  const activeFiltersCount = [filterOnline, filterVerified, filterPremium].filter(Boolean).length;

  if (loading) {
    console.log('⏳ Chargement en cours...');
    return <Loader />;
  }

  console.log('🎨 Rendu avec', filteredFavorites.length, 'favoris');

  return (
    <div className="favorites-page">
      {/* Header */}
      <div className="favorites-header">
        <div>
          <h1 className="page-title">
            <FaHeart className="title-icon" />
            Mes favoris
            <span className="header-count">{favorites.length}</span>
          </h1>
          <p className="page-subtitle">
            {favorites.length} personne{favorites.length > 1 ? 's' : ''} dans votre liste
          </p>
        </div>
        <button 
          className="btn-refresh" 
          onClick={refreshFavorites} 
          disabled={loading}
          title="Actualiser"
        >
          <FaSyncAlt className={loading ? 'spin' : ''} />
        </button>
      </div>

      {/* Recherche */}
      <div className="search-section">
        <div className="search-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Rechercher par nom ou ville..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="search-clear" onClick={() => setSearchTerm('')}>
              <FaTimes />
            </button>
          )}
        </div>

        <button 
          className={`filters-toggle ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter />
          <span>Filtres</span>
          {activeFiltersCount > 0 && (
            <span className="filters-badge">{activeFiltersCount}</span>
          )}
        </button>
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={filterOnline}
                onChange={(e) => setFilterOnline(e.target.checked)}
              />
              <span className="checkbox-custom"></span>
              <span>En ligne uniquement</span>
            </label>

            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={filterVerified}
                onChange={(e) => setFilterVerified(e.target.checked)}
              />
              <span className="checkbox-custom"></span>
              <span>Profils vérifiés</span>
            </label>

            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={filterPremium}
                onChange={(e) => setFilterPremium(e.target.checked)}
              />
              <span className="checkbox-custom"></span>
              <span>Membres premium</span>
            </label>
          </div>

          <div className="sort-group">
            <label>Trier par:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="date">Plus récents</option>
              <option value="name">Ordre alphabétique</option>
              <option value="distance">Les plus proches</option>
            </select>
          </div>

          {(activeFiltersCount > 0 || searchTerm) && (
            <button className="reset-filters" onClick={() => {
              setSearchTerm('');
              setFilterOnline(false);
              setFilterVerified(false);
              setFilterPremium(false);
              setSortBy('date');
            }}>
              Réinitialiser les filtres
            </button>
          )}
        </div>
      )}

      {/* Liste des favoris */}
      {filteredFavorites.length === 0 ? (
        <div className="empty-state">
          <FaHeart className="empty-icon" />
          <h3>
            {favorites.length === 0 ? 'Aucun favori' : 'Aucun résultat'}
          </h3>
          <p>
            {favorites.length === 0 
              ? 'Vous n\'avez pas encore ajouté de profils à vos favoris'
              : 'Aucun profil ne correspond à vos critères de recherche'
            }
          </p>
          {favorites.length === 0 ? (
            <button className="btn-primary" onClick={() => navigate('/discover')}>
              Découvrir des profils
            </button>
          ) : (
            <button className="btn-primary" onClick={() => {
              setSearchTerm('');
              setFilterOnline(false);
              setFilterVerified(false);
              setFilterPremium(false);
            }}>
              Effacer les filtres
            </button>
          )}
        </div>
      ) : (
        <div className="favorites-grid">
          {filteredFavorites.map(fav => (
            <div 
              key={fav.id} 
              className={`favorite-card ${removingId === fav.id ? 'removing' : ''}`}
              onClick={() => navigate(`/profile/${fav.id}`)}
            >
              <div className="favorite-image">
                <img 
                  src={getAvatarUrl(fav)} 
                  alt={fav.pseudo}
                  loading="lazy"
                  onError={(e) => {
                    console.log('❌ Erreur chargement image pour', fav.pseudo);
                    e.target.src = 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e';
                  }}
                />
                
                <div className="favorite-badges">
                  {fav.en_ligne && (
                    <span className="badge-online" title="En ligne">
                      <span className="pulse"></span>
                    </span>
                  )}
                  {fav.is_verified && (
                    <span className="badge-verified" title="Profil vérifié">✓</span>
                  )}
                  {fav.is_premium && (
                    <span className="badge-premium" title="Membre premium">⭐</span>
                  )}
                </div>
              </div>

              <div className="favorite-info">
                <h3>
                  {fav.pseudo}
                  {fav.age && <span className="favorite-age">, {fav.age} ans</span>}
                </h3>
                
                <p className="favorite-location">
                  <FaMapMarkerAlt className="location-icon" />
                  {fav.ville || 'Ville inconnue'}
                  {fav.pays && `, ${fav.pays}`}
                </p>

                {fav.distance && (
                  <p className="favorite-distance">{formatDistance(fav.distance)}</p>
                )}

                <p className="favorite-date">
                  <FaCalendar /> Ajouté le {new Date(fav.date_ajout).toLocaleDateString('fr-FR')}
                </p>
              </div>

              <button 
                className="btn-remove"
                onClick={(e) => removeFavorite(fav.id, e)}
                disabled={removingId === fav.id}
                title="Retirer des favoris"
              >
                {removingId === fav.id ? (
                  <FaSpinner className="spinner" />
                ) : (
                  <FaTrash />
                )}
              </button>

              <button 
                className="btn-message"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/messages?user=${fav.id}`);
                }}
                title="Envoyer un message"
              >
                <FaComment />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Favorites;